/**
 * Delegated Marketplace Functions
 * 
 * Implements seller pre-authorization for NFT transfers using SPL Token delegate authority
 * This allows the platform wallet to transfer NFTs on behalf of sellers after payment
 */

import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { supabase } from '../lib/supabase';
import {
  approveDelegateTx,
  revokeDelegateTx,
  getTokenStandard,
  verifyDelegateActive,
  getExplorerUrl,
  detectTokenProgram,
  assertOnChainDelegateActive,
  classifyTokenForListing,
  verifyOwnershipBasic
} from './tokenHelpers';
import { getCurrentCluster, getRpcEndpoint } from '../config/rpc';

// Platform wallet public key - should match your PLATFORM_WALLET env var
const PLATFORM_WALLET = '8cm597KjGmwTSzadrcVCHVpqwzdRZy1BM2yUrDDEpJcB';

/**
 * List a piece for sale with seller pre-authorization
 * Seller approves the platform wallet as a delegate to transfer the NFT
 */
export async function listPieceWithDelegateAuth({
  wallet,
  piece,
  priceSol,
  connection
}: {
  wallet: any;
  piece: any;
  priceSol: number;
  connection: Connection;
}): Promise<{
  success: boolean;
  authSignature: string;
  piece: any;
}> {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  if (!piece.mint_address) {
    throw new Error('Piece is not minted and cannot be listed for sale');
  }

  console.log('📝 Listing piece with delegate authorization...');
  console.log('Piece:', piece.name);
  console.log('Price:', priceSol, 'SOL');
  console.log('Mint:', piece.mint_address);

  try {
    const mintPubkey = new PublicKey(piece.mint_address);
    const platformWalletPubkey = new PublicKey(PLATFORM_WALLET);

    // Step 1: Classify token and check ownership
    console.log('🔍 Classifying token and verifying ownership...');
    
    const currentCluster = getCurrentCluster();
    const classification = await classifyTokenForListing(connection, mintPubkey, currentCluster);
    
    console.log('Token classification:', {
      program: classification.program,
      standard: classification.standard,
      reason: classification.reason,
      mintExists: classification.mintExists,
      owner: classification.owner,
      supply: classification.supply?.toString(),
      decimals: classification.decimals,
    });

    // Check if mint exists on this cluster
    if (!classification.mintExists) {
      // Check if this is a metadata account (common data error)
      if (classification.owner === 'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s') {
        throw new Error(
          'This piece has corrupted data - the stored address is a metadata account, not a mint address. ' +
          'Please contact support to fix this piece data.'
        );
      }
      
      // If we detected the mint on a different cluster, throw special error
      if (classification.detectedCluster) {
        const error: any = new Error(
          `This NFT is on ${classification.detectedCluster}. Switch networks to continue.`
        );
        error.code = 'WRONG_CLUSTER';
        error.detectedCluster = classification.detectedCluster;
        error.currentCluster = currentCluster;
        throw error;
      }
      
      // Mint not found anywhere
      throw new Error(
        'Mint not found on current network. Please switch to the correct cluster (devnet/mainnet) and try again.'
      );
    }

    // Block programmable NFTs
    if (classification.standard === 'ProgrammableNonFungible') {
      throw new Error(
        'Programmable NFTs are not supported yet. This feature is coming soon.'
      );
    }

    // Verify ownership using basic check (works without metadata, includes try-both fallback)
    const ownership = await verifyOwnershipBasic(
      connection,
      mintPubkey,
      wallet.publicKey,
      classification.program
    );

    console.log('Ownership verification:', {
      connectedWallet: wallet.publicKey.toBase58(),
      owned: ownership.owned,
      amount: ownership.amount,
      ata: ownership.ata.toBase58(),
      programUsed: ownership.programUsed,
      reason: ownership.reason,
    });

    if (!ownership.owned) {
      // Fallback: Check if DB holder_wallet matches connected wallet
      const dbHolderWallet = piece.holder_wallet;
      const connectedWallet = wallet.publicKey.toString();
      
      console.log('🔍 On-chain ownership check failed. Checking DB fallback:', {
        dbHolderWallet,
        connectedWallet,
        match: dbHolderWallet?.toLowerCase() === connectedWallet.toLowerCase(),
      });
      
      if (dbHolderWallet && dbHolderWallet.toLowerCase() === connectedWallet.toLowerCase()) {
        console.warn(
          '⚠️ Ownership override: trusting DB holder_wallet since on-chain check is inconclusive. ' +
          'This may indicate a metadata issue or non-standard token account.'
        );
        // Allow listing to proceed with DB trust
      } else {
        console.error('❌ Ownership verification failed:', {
          connectedWallet,
          dbHolderWallet,
          onChainReason: ownership.reason,
        });
        throw new Error(
          'Only the current on-chain owner can list this item. ' +
          (ownership.reason || 'You do not own this NFT.')
        );
      }
    }

    // Check if we still couldn't detect program even after try-both
    if (classification.program === 'unknown' && !ownership.programUsed) {
      console.error('❌ Could not detect token program after try-both fallback. Mint owner:', classification.owner);
      throw new Error(
        `Could not detect token program. Mint owner: ${classification.owner}. This token may not be compatible.`
      );
    }

    // Log special cases
    if (classification.standard === 'NonFungibleLegacy') {
      console.warn('⚠️ No metadata found; proceeding as legacy 1/1 SPL token.');
    }
    
    if (classification.standard === 'SFT') {
      console.warn(`⚠️ Semi-fungible token detected (supply=${classification.supply}). Will delegate quantity=1 for listing.`);
    }

    // Step 2: Build approve delegate transaction
    console.log('🔐 Building delegate approval transaction...');
    const approveTx = await approveDelegateTx({
      connection,
      seller: wallet.publicKey,
      mint: mintPubkey,
      delegate: platformWalletPubkey
    });

    // Step 3: Request seller signature
    console.log('✍️ Requesting seller signature...');
    const result = await wallet.sendTransaction(approveTx, connection, {
      skipPreflight: false
    });
    
    // Handle different wallet return formats
    const signature = typeof result === 'string' ? result : (result?.signature || result?.txid || result?.transactionSignature || result);

    // Step 4: Wait for confirmation
    console.log('⏳ Confirming transaction...');
    await connection.confirmTransaction(signature, 'confirmed');

    console.log('✅ Delegate approval confirmed:', signature);

    // Step 5: Calculate expiration (7 days from now)
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    // Step 6: Update database
    console.log('💾 Updating database with delegate info...');
    
    const { data: updatedPiece, error: updateError } = await supabase
      .from('pieces')
      .update({
        listing_status: 'listed',
        listing_price: priceSol,
        listed_at: new Date().toISOString(),
        seller_ata: await getSellerAtaString(mintPubkey, wallet.publicKey),
        listing_delegate: PLATFORM_WALLET,
        is_delegate_active: true,
        delegate_expires_at: expiresAt.toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('mint_address', piece.mint_address)
      .select()
      .maybeSingle();

    if (updateError) {
      console.error('❌ Database update failed:', updateError);
      throw new Error(`Failed to update database: ${updateError.message}`);
    }

    if (!updatedPiece) {
      throw new Error('No rows updated in database');
    }

    console.log('✅ Listing complete with delegate authorization!');
    console.log('🔗 View transaction:', getExplorerUrl(signature, 'mainnet'));

    return {
      success: true,
      authSignature: signature,
      piece: updatedPiece
    };

  } catch (error: any) {
    console.error('❌ Listing with delegate auth failed:', error);
    
    // Provide user-friendly error messages
    if (error.message?.includes('Programmable NFT')) {
      throw error; // Pass through our custom error
    }
    
    if (error.message?.includes('User rejected')) {
      throw new Error('You cancelled the transaction');
    }

    if (error.message?.includes('does not own')) {
      throw new Error('You do not own this NFT');
    }

    throw new Error(`Listing failed: ${error.message}`);
  }
}

/**
 * Cancel listing and revoke delegate authority
 */
export async function cancelListingWithRevoke({
  wallet,
  piece,
  connection
}: {
  wallet: any;
  piece: any;
  connection: Connection;
}): Promise<{
  success: boolean;
  revokeSignature: string;
}> {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  if (!piece.mint_address) {
    throw new Error('Piece has no mint address');
  }

  console.log('❌ Cancelling listing and revoking delegate...');
  console.log('Piece:', piece.name);
  console.log('Mint:', piece.mint_address);

  try {
    const mintPubkey = new PublicKey(piece.mint_address);

    // Step 1: Build revoke transaction
    console.log('🔓 Building revoke transaction...');
    const revokeTx = await revokeDelegateTx({
      connection,
      seller: wallet.publicKey,
      mint: mintPubkey
    });

    // Step 2: Request seller signature
    console.log('✍️ Requesting seller signature...');
    const result = await wallet.sendTransaction(revokeTx, connection, {
      skipPreflight: false
    });
    
    // Handle different wallet return formats
    const signature = typeof result === 'string' ? result : (result?.signature || result?.txid || result?.transactionSignature || result);

    // Step 3: Wait for confirmation
    console.log('⏳ Confirming transaction...');
    await connection.confirmTransaction(signature, 'confirmed');

    console.log('✅ Delegate revoked:', signature);

    // Step 4: Update database
    console.log('💾 Updating database...');
    const { error: updateError } = await supabase
      .from('pieces')
      .update({
        listing_status: 'unlisted',
        is_delegate_active: false,
        listing_price: null,
        listed_at: null,
        updated_at: new Date().toISOString()
      })
      .eq('mint_address', piece.mint_address);

    if (updateError) {
      console.error('❌ Database update failed:', updateError);
      throw new Error(`Failed to update database: ${updateError.message}`);
    }

    console.log('✅ Listing cancelled!');
    console.log('🔗 View transaction:', getExplorerUrl(signature, 'mainnet'));

    return {
      success: true,
      revokeSignature: signature
    };

  } catch (error: any) {
    console.error('❌ Cancel listing failed:', error);

    if (error.message?.includes('User rejected')) {
      throw new Error('You cancelled the transaction');
    }

    throw new Error(`Failed to cancel listing: ${error.message}`);
  }
}

/**
 * Verify that delegate authority is still active before accepting payment
 * Checks on-chain and heals database if stale
 */
export async function verifyDelegateBeforePurchase({
  connection,
  mintAddress,
  sellerPubkeyBase58,
  platformDelegateBase58,
}: {
  connection: Connection;
  mintAddress: string;
  sellerPubkeyBase58: string;
  platformDelegateBase58: string;
}): Promise<{
  ok: boolean;
  program?: string;
  sellerAta?: string;
  error?: string;
}> {
  try {
    const mint = new PublicKey(mintAddress);
    const seller = new PublicKey(sellerPubkeyBase58);
    const platform = new PublicKey(platformDelegateBase58);

    const program = await detectTokenProgram(connection, mint);
    const status = await assertOnChainDelegateActive({
      connection,
      mint,
      seller,
      platformDelegate: platform,
      program
    });

    if (status.active) {
      // Heal DB if stale, but don't fail if this fails
      try {
        await supabase.from('pieces')
          .update({
            is_delegate_active: true,
            seller_ata: status.ata,
            listing_delegate: platform.toBase58(),
            token_program: program,
            last_error: null,
          })
          .eq('mint_address', mintAddress);
        console.log('✅ Refreshed delegate authorization in database');
      } catch (dbError) {
        console.warn('⚠️ Failed to heal database, but continuing:', dbError);
      }
      
      return { ok: true, program, sellerAta: status.ata };
    }

    // Chain says inactive -> stop here
    const reason = status.reason ?? 'Delegate authorization is not active';
    return { ok: false, error: reason };

  } catch (error: any) {
    console.error('Delegate verification error:', error);
    return {
      ok: false,
      error: `Verification failed: ${error.message}`
    };
  }
}

/**
 * Helper to get seller ATA as string
 */
async function getSellerAtaString(mint: PublicKey, seller: PublicKey): Promise<string> {
  const { getAssociatedTokenAddress } = await import('@solana/spl-token');
  const ata = await getAssociatedTokenAddress(mint, seller, true);
  return ata.toString();
}

/**
 * Client-side function to initiate server-side delegate transfer
 * This calls an edge function that has access to the platform wallet keypair
 */
export async function requestDelegateTransfer({
  piece,
  sellerWallet,
  buyerWallet,
  paymentSignature,
  priceLamports,
  wallet
}: {
  piece: any;
  sellerWallet: string;
  buyerWallet: string;
  paymentSignature: string;
  priceLamports: number;
  wallet?: any; // Wallet adapter for buyer to sign transactions
}): Promise<{
  success: boolean;
  transferSignature: string;
  updatedPiece?: any;
  explorerUrl?: string;
}> {
  console.log('🎨 Requesting server-side delegate transfer...');
  console.log('📋 Transfer params:', {
    mintAddress: piece.mint_address,
    sellerAddress: sellerWallet,
    buyerAddress: buyerWallet,
    paymentSignature: paymentSignature
  });
  
  // Validate required parameters
  if (!piece.mint_address) {
    throw new Error('Missing mint_address');
  }
  if (!sellerWallet) {
    throw new Error('Missing sellerWallet');
  }
  if (!buyerWallet) {
    throw new Error('Missing buyerWallet');
  }
  if (!paymentSignature) {
    throw new Error('Missing paymentSignature');
  }
  
  // Determine if this is a primary sale (creator selling) or secondary sale (resale)
  const creatorWallet = piece.creator_wallet || piece.creators?.wallet;
  const isPrimarySale = sellerWallet === creatorWallet;
  
  console.log('📊 Sale type:', isPrimarySale ? 'PRIMARY (creator selling)' : 'SECONDARY (resale)');
  
  try {
    let response;
    
    if (isPrimarySale) {
      // PRIMARY SALE: Use transfer-nft (no listing required)
      console.log('💰 Using transfer-nft for primary sale');
      const requestBody = {
        mintAddress: piece.mint_address,
        sellerAddress: sellerWallet,
        buyerAddress: buyerWallet,
        transactionSignature: paymentSignature
      };
      
      console.log('📤 Sending to transfer-nft:', requestBody);
      console.log('  mintAddress:', requestBody.mintAddress);
      console.log('  sellerAddress:', requestBody.sellerAddress);
      console.log('  buyerAddress:', requestBody.buyerAddress);
      console.log('  transactionSignature:', requestBody.transactionSignature);
      
      // Validate each parameter
      if (!requestBody.mintAddress) {
        throw new Error('Missing mintAddress parameter');
      }
      if (!requestBody.sellerAddress) {
        throw new Error('Missing sellerAddress parameter'); 
      }
      if (!requestBody.buyerAddress) {
        throw new Error('Missing buyerAddress parameter');
      }
      if (!requestBody.transactionSignature) {
        throw new Error('Missing transactionSignature (paymentSignature) parameter');
      }
      
      response = await supabase.functions.invoke('transfer-nft', {
        body: requestBody
      });
    } else {
      // SECONDARY SALE: Use execute-delegate-transfer (requires listing)
      console.log('🔄 Using execute-delegate-transfer for secondary sale');
      response = await supabase.functions.invoke('execute-delegate-transfer', {
        body: {
          mint_address: piece.mint_address,
          seller_wallet: sellerWallet,
          buyer_wallet: buyerWallet,
          payment_signature: paymentSignature,
          price_lamports: priceLamports  // Required by edge function
        }
      });
    }

    console.log('📥 Edge function full response:', {
      data: response.data,
      error: response.error,
      status: (response as any).status
    });
    
    // Check data first - edge functions return error details in data even when there's an error
    const data = response.data;
    
    if (data && !data.success) {
      console.error('❌ Transfer failed - error from edge function:', data.error);
      throw new Error(data.error || data.message || 'Transfer failed');
    }
    
    // Check if transaction requires buyer signature (new flow where buyer pays gas)
    if (data && data.requiresBuyerSignature && data.transaction) {
      console.log('📝 Transaction requires buyer signature - buyer will pay gas fees');
      console.log('📦 Transaction data received:', {
        hasTransaction: !!data.transaction,
        transactionLength: data.transaction?.length,
        hasBlockhash: !!data.blockhash,
        hasLastValidBlockHeight: !!data.lastValidBlockHeight
      });
      
      if (!wallet) {
        throw new Error('Wallet not provided for signing transaction');
      }
      
      console.log('🔍 Checking wallet capabilities:', {
        hasSendTransaction: !!wallet.sendTransaction,
        hasSignTransaction: !!wallet.signTransaction,
        hasSignAndSendTransaction: !!wallet.signAndSendTransaction,
        hasPublicKey: !!wallet.publicKey,
        walletKeys: Object.keys(wallet)
      });
      
      if (!wallet.sendTransaction && !wallet.signTransaction && !wallet.signAndSendTransaction) {
        throw new Error('Wallet does not support transaction signing (missing signTransaction/sendTransaction method)');
      }
      
      // Import necessary Solana libraries
      const { Transaction, Connection } = await import('@solana/web3.js');
      
      // Deserialize the transaction
      const transactionBuffer = Uint8Array.from(atob(data.transaction), c => c.charCodeAt(0));
      const transaction = Transaction.from(transactionBuffer);
      
      console.log('✍️ Requesting buyer signature...');
      
      // Create connection with proper Helius RPC
      const rpcEndpoint = getRpcEndpoint();
      console.log('🌐 Using RPC endpoint:', rpcEndpoint.includes('helius') ? 'Helius (premium)' : 'Public');
      const connection = new Connection(rpcEndpoint, 'confirmed');
      
      // Try pre-simulation to reduce Phantom warnings (completely silent if it fails)
      try {
        console.log('🔍 Attempting pre-simulation...');
        const simulation = await connection.simulateTransaction(transaction, {
          sigVerify: false,  // Don't verify signatures (partially signed)
          commitment: 'confirmed',
          replaceRecentBlockhash: true,
        });
        
        if (!simulation.value.err) {
          console.log('✅ Pre-simulation passed successfully');
        } else {
          console.log('⚠️ Pre-simulation returned error (expected for partial sigs):', simulation.value.err);
        }
      } catch (simError: any) {
        // Completely ignore simulation errors - this is expected for partially-signed transactions
        console.log('ℹ️ Pre-simulation skipped (partially-signed transaction)');
      }
      
      let signature;
      
      // For partially-signed transactions (multi-signer), PREFER signTransaction (Phantom recommendation)
      // This transaction is already signed by platform wallet, buyer adds their signature
      if (wallet.signTransaction) {
        console.log('✍️ Using wallet.signTransaction (PREFERRED for multi-signer per Phantom guidelines)');
        const signedTransaction = await wallet.signTransaction(transaction);
        
        console.log('📤 Sending fully-signed transaction to network...');
        signature = await connection.sendRawTransaction(signedTransaction.serialize(), {
          skipPreflight: false,
          preflightCommitment: 'confirmed'
        });
      }
      // Fallback: sendTransaction (if wallet doesn't support signTransaction)
      else if (wallet.sendTransaction) {
        console.log('📤 Using wallet.sendTransaction (fallback)');
        const result = await wallet.sendTransaction(transaction, connection, {
          skipPreflight: false,
          preflightCommitment: 'confirmed'
        });
        signature = typeof result === 'string' ? result : (result?.signature || result?.txid || result?.transactionSignature || result);
      }
      // Last resort: signAndSendTransaction
      else if (wallet.signAndSendTransaction) {
        console.log('📤 Using wallet.signAndSendTransaction (last resort)');
        const result = await wallet.signAndSendTransaction(transaction);
        signature = typeof result === 'string' ? result : result.signature;
      }
      else {
        throw new Error('Wallet does not support any signing methods');
      }
      
      console.log('⏳ Confirming transaction:', signature);
      
      // Use more robust confirmation with longer timeout for mainnet
      try {
        // Try standard confirmation first with timeout
        const confirmationTimeout = 90000; // 90 seconds for mainnet
        await Promise.race([
          connection.confirmTransaction({
            signature,
            blockhash: data.blockhash,
            lastValidBlockHeight: data.lastValidBlockHeight
          }, 'confirmed'),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Confirmation timeout')), confirmationTimeout)
          )
        ]);
        console.log('✅ Transfer confirmed by buyer:', signature);
      } catch (confirmError: any) {
        // If confirmation times out, poll for status instead
        console.warn('⚠️ Standard confirmation failed, polling status:', confirmError.message);
        
        // Poll transaction status with retries
        let confirmed = false;
        const maxPolls = 30; // 30 polls = 60 seconds
        for (let i = 0; i < maxPolls && !confirmed; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2s between polls
          
          // Try getSignatureStatus first (lightweight)
          try {
            const status = await connection.getSignatureStatus(signature);
            console.log(`🔍 Poll ${i + 1}/${maxPolls} - Status:`, status?.value?.confirmationStatus);
            
            if (status?.value?.confirmationStatus === 'confirmed' || 
                status?.value?.confirmationStatus === 'finalized') {
              confirmed = true;
              console.log('✅ Transfer confirmed via status polling:', signature);
              break;
            }
            
            if (status?.value?.err) {
              throw new Error(`Transaction failed: ${JSON.stringify(status.value.err)}`);
            }
            
            // If status is undefined/null after 10 polls, try fetching the full transaction
            if (!status?.value && i >= 10) {
              console.log(`⚠️ Status undefined after ${i + 1} polls, checking full transaction...`);
              const tx = await connection.getTransaction(signature, {
                commitment: 'confirmed',
                maxSupportedTransactionVersion: 0
              });
              
              if (tx && !tx.meta?.err) {
                confirmed = true;
                console.log('✅ Transfer confirmed via transaction fetch:', signature);
                break;
              } else if (tx?.meta?.err) {
                throw new Error(`Transaction failed: ${JSON.stringify(tx.meta.err)}`);
              }
            }
          } catch (pollError: any) {
            console.warn(`⚠️ Poll ${i + 1} error:`, pollError.message);
            // Continue polling on RPC errors
            if (i === maxPolls - 1) {
              // On last poll, try one final transaction fetch
              console.log('🔍 Final attempt: fetching full transaction...');
              try {
                const tx = await connection.getTransaction(signature, {
                  commitment: 'confirmed',
                  maxSupportedTransactionVersion: 0
                });
                
                if (tx && !tx.meta?.err) {
                  confirmed = true;
                  console.log('✅ Transfer confirmed on final attempt:', signature);
                  break;
                } else if (tx?.meta?.err) {
                  throw new Error(`Transaction failed: ${JSON.stringify(tx.meta.err)}`);
                }
              } catch (finalError: any) {
                console.error('❌ Final confirmation attempt failed:', finalError.message);
              }
            }
          }
        }
        
        if (!confirmed) {
          // Transaction might have succeeded despite timeout - provide helpful message
          throw new Error(`Transaction confirmation timeout. Check explorer: https://solscan.io/tx/${signature}`);
        }
      }
      
      // STEP 1: Close ALL active listings for this mint FIRST (prevents stale listings)
      console.log('🔧 Closing all active listings for mint:', piece.mint_address);
      const { data: closedListings, error: closeListingsError } = await supabase
        .from('listings')
        .update({
          status: 'filled',
          buyer_wallet: buyerWallet,
          transfer_signature: signature,
          filled_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('mint_address', piece.mint_address)
        .eq('status', 'active')
        .select('id');
      
      if (closeListingsError) {
        console.error('❌ Failed to close active listings:', closeListingsError);
        // Don't fail the whole transaction, but log it
      } else if (closedListings && closedListings.length > 0) {
        console.log(`✅ Closed ${closedListings.length} active listing(s):`, closedListings.map(l => l.id));
      } else {
        console.log('ℹ️ No active listings found to close (OK if this is a primary sale)');
      }
      
      // STEP 2: Update piece to clean, unlisted state with new owner
      const { data: updatedPiece, error: pieceUpdateError } = await supabase
        .from('pieces')
        .update({
          holder_wallet: buyerWallet,
          listing_status: null,  // Clear status - buyer can now list it fresh
          listing_price: null,  // Clear listing price
          is_delegate_active: false,  // Clear delegate
          listing_delegate: null,  // Clear delegate wallet
          seller_ata: null,  // Clear seller ATA
          delegate_expires_at: null,  // Clear expiration
          sold_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('mint_address', piece.mint_address)
        .select()
        .single();
      
      if (pieceUpdateError) {
        console.error('❌ Failed to update piece ownership:', pieceUpdateError);
        throw new Error(`Database update failed: ${pieceUpdateError.message}`);
      }
      
      console.log('✅ Database updated with transfer signature');
      console.log('✅ Updated piece:', updatedPiece);
      console.log('✅ All active listings closed and piece ownership finalized');
      
      // Log the sale activity to nft_activity table
      try {
        const now = new Date();
        const timestamp = Math.floor(now.getTime() / 1000);
        
        const activityData = {
          mint_address: piece.mint_address,
          event_type: 'sale',
          from_address: sellerWallet,
          to_address: buyerWallet,
          price: priceLamports / 1e9, // Convert lamports to SOL
          amount_lamports: priceLamports,
          signature: signature,
          tx_sig: signature,
          transaction_hash: signature,
          block_time: now.toISOString(),
          created_at: now.toISOString(),
          event_timestamp: timestamp,
          source: 'app',
          slot: 0
        };
        
        console.log('📝 [ACTIVITY] Logging sale activity...');
        console.log('📝 [ACTIVITY] Data:', JSON.stringify(activityData, null, 2));
        
        const { data: insertedData, error: activityError } = await supabase.from('nft_activity').insert(activityData).select();
        
        if (activityError) {
          console.error('❌ [ACTIVITY] Failed to log sale activity!');
          console.error('❌ [ACTIVITY] Error:', activityError);
          console.error('❌ [ACTIVITY] Error message:', activityError.message);
          console.error('❌ [ACTIVITY] Error details:', activityError.details);
          console.error('❌ [ACTIVITY] Error hint:', activityError.hint);
          console.error('❌ [ACTIVITY] Data that failed:', activityData);
        } else {
          console.log('✅ [ACTIVITY] Sale activity logged successfully!');
          console.log('✅ [ACTIVITY] Inserted data:', insertedData);
          console.log('✅ [ACTIVITY] Sale from:', sellerWallet, 'to:', buyerWallet);
        }
      } catch (activityError) {
        console.error('❌ [ACTIVITY] Exception logging sale activity!');
        console.error('❌ [ACTIVITY] Exception:', activityError);
        console.error('❌ [ACTIVITY] Stack:', activityError instanceof Error ? activityError.stack : 'No stack trace');
      }
      
      return {
        success: true,
        transferSignature: signature,
        updatedPiece: updatedPiece
      };
    }
    
    // If no data but there's an error, handle generic error
    if (response.error) {
      console.error('❌ Edge function error:', response.error);
      console.error('❌ Error message:', response.error.message);
      console.error('❌ Error context:', (response.error as any).context);
      console.error('❌ Full error object:', JSON.stringify(response.error, null, 2));
      
      // Try to extract error from context - the body is a ReadableStream
      const context = (response.error as any).context;
      if (context && context.body) {
        try {
          // Check if it's a Response object with body stream
          if (context.body instanceof ReadableStream || typeof context.body.getReader === 'function') {
            console.log('📖 Reading error stream...');
            const reader = context.body.getReader();
            const chunks: Uint8Array[] = [];
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              chunks.push(value);
            }
            
            const decoder = new TextDecoder();
            const responseText = decoder.decode(
              chunks.reduce((acc, chunk) => {
                const tmp = new Uint8Array(acc.length + chunk.length);
                tmp.set(acc);
                tmp.set(chunk, acc.length);
                return tmp;
              }, new Uint8Array())
            );
            
            console.error('❌ Error response text:', responseText);
            const errorBody = JSON.parse(responseText);
            console.error('❌ Parsed error body:', errorBody);
            if (errorBody.error) {
              throw new Error(errorBody.error);
            }
          } else {
            const errorBody = typeof context.body === 'string' ? JSON.parse(context.body) : context.body;
            console.error('❌ Error body from context:', errorBody);
            if (errorBody.error) {
              throw new Error(errorBody.error);
            }
          }
        } catch (e) {
          console.error('Could not parse error body:', e);
        }
      }
      
      const errorMsg = response.error.message || 'Server-side transfer failed';
      throw new Error(errorMsg);
    }
    
    if (!data) {
      throw new Error('No response data from transfer function');
    }

    // Handle different response formats from different functions
    const transferSig = data.signature || data.transferSignature;
    console.log('✅ Delegate transfer completed:', transferSig);
    
    if (data.idempotent) {
      console.log('ℹ️ Payment was already processed (idempotent)');
    }

    return {
      success: true,
      transferSignature: transferSig,
      explorerUrl: data.explorer_url || data.explorerUrl || `https://explorer.solana.com/tx/${transferSig}`
    };

  } catch (error: any) {
    console.error('❌ Delegate transfer request failed:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      fullError: error
    });
    throw new Error(`Failed to execute transfer: ${error.message}`);
  }
}

/**
 * Check if a piece requires re-authorization (expired or revoked)
 */
export function needsReauthorization(piece: any): boolean {
  if (!piece.is_delegate_active) {
    return true;
  }

  if (piece.delegate_expires_at) {
    const expiresAt = new Date(piece.delegate_expires_at);
    if (expiresAt < new Date()) {
      return true;
    }
  }

  return false;
}

